/**
 * CHURCH TREE — PURE church tree helpers with materialized path.
 *
 * `church.path` = ids from root to node joined by '/'
 * (`root/daughter/granddaughter`). ALWAYS computed by server; client uses it for
 * subtree queries WITHOUT recursion on SQLite:
 *
 *   WHERE path = ? OR path LIKE ? || '/%'
 *
 * Full design: docs/CHURCH_TREE_GUIDE.md.
 */

export const CHURCH_PATH_SEPARATOR = "/";

/** Path of new church from parent path (null -> root). */
export function buildChurchPath(parentPath: string | null, churchId: string): string {
  return parentPath ? `${parentPath}${CHURCH_PATH_SEPARATOR}${churchId}` : churchId;
}

/** IDs from root to node (the decomposed path itself). */
export function pathToAncestorIds(path: string): string[] {
  return path.length === 0 ? [] : path.split(CHURCH_PATH_SEPARATOR);
}

/** Depth of node (root = 0). */
export function pathDepth(path: string): number {
  return Math.max(0, pathToAncestorIds(path).length - 1);
}

/** ID of tree root that node belongs to. */
export function pathRootId(path: string): string | null {
  return pathToAncestorIds(path)[0] ?? null;
}

/**
 * Is `candidate` INSIDE subtree of `ancestor`? (includes ancestor
 * itself). Prefix comparison with separator — never simple substring
 * (avoids false positives between IDs sharing prefix).
 */
export function isInSubtree(ancestorPath: string, candidatePath: string): boolean {
  return (
    candidatePath === ancestorPath || candidatePath.startsWith(ancestorPath + CHURCH_PATH_SEPARATOR)
  );
}

/**
 * SQL subtree clause (SQLite and Postgres): `(path = ? OR path LIKE ?)`.
 * Returns two parameters in order. '%' is OUTSIDE escaped value.
 */
export function subtreeWhereParams(ancestorPath: string): [string, string] {
  return [ancestorPath, `${ancestorPath}${CHURCH_PATH_SEPARATOR}%`];
}

/** SQL subtree filter to interpolate into parameterized query. */
export const SUBTREE_WHERE_SQL = "(path = ? OR path LIKE ?)";

/**
 * New path of a node (and descendants) upon RE-ANCHORING subtree:
 * replaces prefix `oldSubtreeRoot` with `newSubtreeRoot`. Returns null if
 * path does not belong to moved subtree (should not touch).
 */
export function rebasePath(
  path: string,
  oldSubtreeRoot: string,
  newSubtreeRoot: string,
): string | null {
  if (!isInSubtree(oldSubtreeRoot, path)) {
    return null;
  }
  return newSubtreeRoot + path.slice(oldSubtreeRoot.length);
}

/**
 * Would this re-anchoring move node under its OWN subtree? (cycle).
 * `selfPath` = current node path; `newParentPath` = destination parent path.
 */
export function wouldCreateCycle(selfPath: string, newParentPath: string): boolean {
  return isInSubtree(selfPath, newParentPath);
}

/** Minimal church row to assemble tree in UI. */
export interface ChurchTreeRow {
  id: string;
  parent_id: string | null;
  path: string;
  name: string;
}

export interface ChurchTreeNode<T extends ChurchTreeRow = ChurchTreeRow> {
  row: T;
  depth: number;
  children: ChurchTreeNode<T>[];
}

/**
 * Builds forest (multiple possible roots) from flat rows,
 * ordering children by name. Orphans (unsynced parent — e.g.
 * outside granted scope) are promoted to roots: UI shows subtree
 * THAT account can view.
 */
export function buildChurchForest<T extends ChurchTreeRow>(
  rows: readonly T[],
): ChurchTreeNode<T>[] {
  const byId = new Map<string, ChurchTreeNode<T>>();
  for (const row of rows) {
    byId.set(row.id, { row, depth: 0, children: [] });
  }
  const roots: ChurchTreeNode<T>[] = [];
  for (const node of byId.values()) {
    const parent = node.row.parent_id ? byId.get(node.row.parent_id) : undefined;
    if (parent && parent !== node) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  sortForest(roots, 0);
  return roots;
}

function sortForest<T extends ChurchTreeRow>(nodes: ChurchTreeNode<T>[], depth: number): void {
  nodes.sort((a, b) => a.row.name.localeCompare(b.row.name));
  for (const n of nodes) {
    n.depth = depth;
    sortForest(n.children, depth + 1);
  }
}
